/**
 * Light/dark theme state. Tailwind handles the DOM side via the `dark`
 * class on <html> (darkMode: "class"); Recharts components read the
 * active mode from this context to pick the matching `chartThemes`
 * variant (SVG attributes can't use Tailwind classes).
 *
 * Resolution order: localStorage `slot.theme` → OS preference → light.
 * The inline script in index.html applies the same logic pre-paint so
 * there's no flash of the wrong theme before React mounts.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "slot.theme";

export function getInitialTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  } catch {
    // localStorage unavailable (e.g. sandboxed iframe) — fall through.
  }
  return "light";
}

interface ThemeContextValue {
  theme: ThemeMode;
  toggleTheme: () => void;
}

// Default lets components render outside a provider (unit tests).
const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ThemeMode>(getInitialTheme);
  // Only persist once the user has made an explicit choice (stored value
  // or a toggle click). Persisting the OS-derived theme on first mount
  // would freeze it, so later OS preference changes could never apply.
  const [hasExplicitPreference, setHasExplicitPreference] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === "light" || stored === "dark";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    if (!hasExplicitPreference) return;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Persistence is best-effort.
    }
  }, [hasExplicitPreference, theme]);

  const toggleTheme = useCallback(() => {
    setHasExplicitPreference(true);
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
